// Code generated by apic; DO NOT EDIT.
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useMemo } from "react";

/** A precomputed set of code samples for one surface item (endpoint/op/tool). */
export interface SurfaceSampleSet {
  curl: string;
  go: string;
  python: string;
  typescript: string;
}

/** Tab identifiers for the code-sample strip. */
type SampleTab = "curl" | "go" | "python" | "typescript";

const SURFACE_TABS: Array<{ id: SampleTab; label: string }> = [
  { id: "curl",       label: "curl" },
  { id: "go",         label: "Go" },
  { id: "python",     label: "Python" },
  { id: "typescript", label: "TypeScript" },
];

/**
 * SurfaceSamples renders a four-tab code-sample strip from a precomputed
 * SurfaceSampleSet.  Unlike CodeSamples (which computes REST samples on-demand),
 * this component accepts an already-built set of strings so it is reusable across
 * all surfaces: GraphQL ops, WebSocket endpoints, and MCP tools.
 *
 * Props:
 *   samples   - precomputed { curl, go, python, typescript } sample strings.
 *   className - extra CSS class appended to the root element.
 *
 * Use useMemo in the parent to avoid rebuilding the samples on every render.
 * The copy-to-clipboard button is guarded the same way as CodeSamples (no
 * global navigator access at module load).
 *
 * No web-global dependencies at module load time (RN-safe).
 */
export function SurfaceSamples(props: {
  samples: SurfaceSampleSet;
  className?: string;
}): JSX.Element {
  const { samples, className } = props;
  const [activeTab, setActiveTab] = useState<SampleTab>("curl");
  const [copied, setCopied] = useState<boolean>(false);

  const root = ["apic-ui-code-samples", className].filter(Boolean).join(" ");

  const sample = useMemo((): string => {
    switch (activeTab) {
      case "curl":       return samples.curl;
      case "go":         return samples.go;
      case "python":     return samples.python;
      case "typescript": return samples.typescript;
    }
  }, [activeTab, samples]);

  function handleCopy(): void {
    // Guard: navigator.clipboard is only available in secure browser contexts.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const g = globalThis as any;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const nav = g.navigator as { clipboard?: { writeText?: (s: string) => Promise<void> } } | undefined;
    if (nav !== undefined && nav.clipboard !== undefined && typeof nav.clipboard.writeText === "function") {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      nav.clipboard.writeText(sample).then(
        () => {
          setCopied(true);
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
          (g.setTimeout as ((fn: () => void, ms: number) => void))(() => { setCopied(false); }, 1500);
        },
        () => { /* clipboard write rejected — ignore silently */ },
      );
    }
  }

  return (
    <div className={root}>
      {/* Tab strip */}
      <div className="apic-ui-code-samples-tabs" role="tablist">
        {SURFACE_TABS.map((tab) => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={activeTab === tab.id}
            className={
              "apic-ui-code-tab" +
              (activeTab === tab.id ? " apic-ui-code-tab--active" : "")
            }
            onClick={() => { setActiveTab(tab.id); setCopied(false); }}
          >
            {tab.label}
          </button>
        ))}

        {/* Copy button */}
        <button
          className={"apic-ui-code-copy" + (copied ? " apic-ui-code-copy--copied" : "")}
          aria-label={copied ? "Copied!" : "Copy to clipboard"}
          onClick={handleCopy}
        >
          {copied ? "✓ Copied" : "Copy"}
        </button>
      </div>

      {/* Code panel */}
      <pre className={"apic-ui-code-panel apic-ui-code-panel--" + activeTab}>
        <code>{sample}</code>
      </pre>
    </div>
  );
}
